// Admin — Delete Shift modal.
// BE: DELETE /attendance/shifts/:id — ca chưa có chấm công thì xoá hẳn,
// ca đã có chấm công thì chỉ tắt để giữ lịch sử (xem AttendanceService.deleteShift).

import { useState } from 'react';
import { Modal, notification } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { DeleteShiftResponse, Shift } from 'types/shift';
import { mapShiftError } from '../helpers/shift-error';
import { nameWithoutTime } from '../helpers/shift-time';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import styles from '../styles.module.scss';

interface Props {
  open: boolean;
  shift: Shift | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteShiftModal = ({ open, shift, onClose, onSuccess }: Props) => {
  const [loading, setLoading] = useState(false);

  const departmentLabel = shift?.department
    ? DEPARTMENT_LABEL[shift.department] || shift.department
    : 'chưa gán phòng ban';
  const timeLabel = shift ? `${shift.startTime} → ${shift.endTime}` : '';

  const handleDelete = async () => {
    if (!shift) return;
    setLoading(true);
    try {
      const res: any = await AxiosUtils._delete(`${API.attendanceShifts}/${shift.id}`, undefined);
      const body: DeleteShiftResponse = res?.data ?? res;
      if (body?.mode === 'archived') {
        notification.info({
          message: `Đã tắt ca ${timeLabel}.`,
          description: `Ca này đã có ${body.attendanceCount} lượt chấm công nên được giữ lại ở trạng thái Tắt để không mất lịch sử. Bật "Hiện ca đã tắt" để xem.`,
        });
      } else {
        notification.success({ message: `Đã xoá ca ${timeLabel}.` });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const raw = Array.isArray(err?.message) ? err.message[0] : err?.message;
      if (err?.statusCode === 404 || raw?.includes?.('SHIFT_NOT_FOUND')) {
        notification.warning({ message: 'Ca làm không tồn tại hoặc đã bị xoá.' });
        onSuccess();
        onClose();
        return;
      }
      notification.error({ message: mapShiftError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={loading ? undefined : onClose}
      footer={null}
      destroyOnClose
      width={460}
      closable={!loading}
      title={
        <span className={styles.dangerTitle}>
          <ExclamationCircleFilled /> Xoá ca làm
        </span>
      }
    >
      {shift && (
        <>
          <div className={styles.deleteSummary}>
            <span className={styles.deleteName}>{nameWithoutTime(shift.name) || shift.name}</span>
            <span className={styles.deleteTime}>{timeLabel}</span>
            <span className={styles.deleteMeta}>Phòng ban: {departmentLabel}</span>
          </div>
          <ul className={styles.deleteNotes}>
            <li>Nhân viên phòng {departmentLabel} sẽ không còn thấy ca này trên app.</li>
            <li>Lịch phân ca từ ngày mai trở đi của ca này bị gỡ.</li>
            <li>
              Nếu ca <b>đã có dữ liệu chấm công</b>, hệ thống chỉ chuyển ca sang <b>Tắt</b> để giữ
              nguyên lịch sử và bảng lương.
            </li>
          </ul>
          <p className={styles.deleteQuestion}>Bạn có chắc muốn xoá ca này?</p>
          <div className={styles.modalFooter}>
            <CustomButton text="Không" bgColor="primary-grey" onClick={onClose} disabled={loading} />
            <CustomButton text="Có" bgColor="primary-red" onClick={handleDelete} loading={loading} />
          </div>
        </>
      )}
    </Modal>
  );
};

export default DeleteShiftModal;
